import type { MeResponse } from './generated';
import { request } from './client';
import { personNameOrNull } from './personNames';
import { normalizeStanding, type MemberStanding } from './memberStanding';

export type Tier = 'SUBSCRIBER' | 'GUEST' | 'NONE';
export type PrincipalType = 'PERSON' | 'PLATFORM_SUPPORT';

export type AiEnabledSource = 'DEFAULT' | 'PERSON';

/**
 * How payees are named (TMMAPI-0193 U3): `FREE_FIRST` (the default) names from the person's rules,
 * known merchants and the bank's text before AI; `AI_ALL` has AI check every name.
 */
export type PayeeNamingMode = 'FREE_FIRST' | 'AI_ALL';

export interface MeUser {
  id: number | null;
  displayName: string;
  email: string;
  currency: string | null;
  timezone: string | null;
  locale: string | null;
  /**
   * Meaningful only when `id` is non-null (a real users row). When `id` is null there is no
   * profile yet — do not render an AI affordance from this value.
   */
  aiEnabled: boolean;
  /** Present only with a profile; `DEFAULT` = seeded from the tenant, `PERSON` = they chose. */
  aiEnabledSource: AiEnabledSource | null;
  aiMonthlyCapMinor: number | null;
  /**
   * Keep my contributions out of AI (TMMAPI-0181 U1, MM-D-146): partners' assistant sessions don't
   * use what this person puts into their virtual accounts. False when not served.
   */
  aiContributionsExcluded: boolean;
  /** `FREE_FIRST` when not served (the server's default). */
  payeeNamingMode: PayeeNamingMode;
  /**
   * The member's country, ISO 3166-1 alpha-2 upper case (TMMAPI-0195 U2, MM-D-152): their own, else
   * the tenant's as served; null when none. Never derived from a currency. Institution search reads
   * it first.
   */
  country: string | null;
  /**
   * Where `country` came from (TMMAPI-0196 U5): `MEMBER` (they set it) or `TENANT` (the tenant's
   * default; not served yet). Null when `country` is, or when the wire sends no known value.
   */
  countrySource: CountrySource | null;
}

export type CountrySource = 'MEMBER' | 'TENANT';

export interface DeletionRequested {
  on: string;
  coolingOffUntil: string;
}

export interface MeVirtualAccount extends MemberStanding {
  id: number;
  name: string;
  /** The owner's display name; `null` when they have none (render with `personName`). */
  ownerDisplayName: string | null;
  /**
   * The owner's user id (TMMAPI-0142, required by the contract): a grouping and equality key only,
   * never rendered or sent.
   */
  ownerUserId: number;
  /** The virtual account OWNER's operating currency — money inside it renders in this (§7.9). */
  currency: string;
  mine: boolean;
  sharedBy: string | null;
  /**
   * Who an own virtual account is shared with (TMMAPI-0186 U3): the display names holding an ACTIVE
   * share, in share order. Served on the caller's own virtual accounts only; empty on one shared
   * with them (its `ownerDisplayName` says whose) and on an own one shared with no one.
   */
  sharedWithNames: string[];
  /** The pending invitations on an own virtual account, by name (TMMAPI-0186 U3); else empty. */
  invitedNames: string[];
}

export interface MeSponsor {
  userId: number;
  /** `null` when the sponsor has no display name (render with `personName`). */
  displayName: string | null;
}

export interface MeOnboarding {
  completedAt: string | null;
  dismissedAt: string | null;
  lastStep: string | null;
}

export interface Me {
  principalType: PrincipalType;
  tier: Tier;
  user: MeUser;
  virtualAccounts: MeVirtualAccount[];
  sponsors: MeSponsor[];
  onboarding: MeOnboarding | null;
  locked: boolean;
  lockedAt: string | null;
  frozen: boolean;
  frozenAt: string | null;
  deletionRequested: DeletionRequested | null;
  /** Civil date the trial ends. Present only while trialing; null otherwise or when not a date. */
  trialEndsOn: string | null;
  /**
   * The currency can no longer change: something is denominated in it (TMMAPI-0143). Null when not
   * served.
   */
  currencyLocked: boolean | null;
}

export class MeShapeError extends Error {
  override readonly name = 'MeShapeError';
}

/** An ISO 3166-1 alpha-2 code, upper case; anything else is null (never a crash). */
export function normalizeCountry(value: string | null | undefined): string | null {
  if (typeof value !== 'string') return null;
  const code = value.trim().toUpperCase();
  return /^[A-Z]{2}$/.test(code) ? code : null;
}

const TIERS: ReadonlySet<string> = new Set(['SUBSCRIBER', 'GUEST', 'NONE']);
const CIVIL_DATE = /^(\d{4})-(0[1-9]|1[0-2])-(0[1-9]|[12]\d|3[01])$/;

function isTier(value: string): value is Tier {
  return TIERS.has(value);
}

export function normalizeMe(w: MeResponse): Me {
  const principalType: PrincipalType =
    w.principalType === 'PLATFORM_SUPPORT' ? 'PLATFORM_SUPPORT' : 'PERSON';
  if (!isTier(w.tier)) {
    throw new MeShapeError(`Unexpected tier: ${String(w.tier)}`);
  }
  const u = w.user;
  const userId = u.id ?? null;
  // aiEnabled / aiEnabledSource are meaningful only when a users row exists (id present).
  const aiEnabled = userId !== null ? (u.aiEnabled ?? false) : false;
  const aiEnabledSource: AiEnabledSource | null =
    userId === null
      ? null
      : u.aiEnabledSource === 'DEFAULT' || u.aiEnabledSource === 'PERSON'
        ? u.aiEnabledSource
        : null;
  const country = normalizeCountry(u.country);
  const deletion = w.deletionRequested;
  const onboarding = w.onboarding;
  return {
    principalType,
    tier: w.tier,
    user: {
      id: userId,
      displayName:
        u.displayName !== undefined && u.displayName.length > 0 ? u.displayName : u.email,
      email: u.email,
      currency: u.currency ?? null,
      timezone: u.timezone ?? null,
      locale: u.locale ?? null,
      aiEnabled,
      aiEnabledSource,
      aiMonthlyCapMinor: u.aiMonthlyCapMinor ?? null,
      aiContributionsExcluded: u.aiContributionsExcluded ?? false,
      payeeNamingMode: u.payeeNamingMode === 'AI_ALL' ? 'AI_ALL' : 'FREE_FIRST',
      country,
      countrySource:
        country !== null && (u.countrySource === 'MEMBER' || u.countrySource === 'TENANT')
          ? u.countrySource
          : null,
    },
    locked: w.locked,
    lockedAt: w.lockedAt ?? null,
    frozen: w.frozen,
    frozenAt: w.frozenAt ?? null,
    deletionRequested: !deletion
      ? null
      : { on: deletion.on, coolingOffUntil: deletion.coolingOffUntil },
    onboarding: !onboarding
      ? null
      : {
          completedAt: onboarding.completedAt ?? null,
          dismissedAt: onboarding.dismissedAt ?? null,
          lastStep: onboarding.lastStep ?? null,
        },
    virtualAccounts: w.virtualAccounts.map((d) => ({
      id: d.id,
      name: d.name,
      ownerDisplayName: personNameOrNull(d.ownerDisplayName),
      ownerUserId: d.ownerUserId,
      currency: d.currency ?? '',
      mine: d.mine,
      sharedBy: personNameOrNull(d.sharedBy),
      sharedWithNames: d.sharedWithNames ?? [],
      invitedNames: d.invitedNames ?? [],
      ...normalizeStanding(d),
    })),
    trialEndsOn:
      w.trialEndsOn !== undefined && CIVIL_DATE.test(w.trialEndsOn) ? w.trialEndsOn : null,
    currencyLocked: w.currencyLocked ?? null,
    sponsors: w.sponsors.map((s) => ({
      userId: s.userId,
      displayName: personNameOrNull(s.displayName),
    })),
  };
}

export async function getMe(): Promise<Me> {
  return normalizeMe(await request<MeResponse>('GET', '/v1/me'));
}
